import { getAuth, onAuthStateChanged } from "firebase/auth";
import { createContext, useEffect, useState } from "react";
import { setSessionHint } from "../auth/sessionHint";

// How long to wait for Firebase to report auth state before assuming it never
// will. onAuthStateChanged normally fires almost immediately (it reads persisted
// state locally), so this only trips when something is genuinely wrong.
const AUTH_RESOLUTION_TIMEOUT_MS = 10_000;

// null means logged out.
// undefined means initial loading.
// We don't get the whole FirestoreDocUser, because we should
// never user the stripe_customer_id from the client side
export const AuthContext = createContext<
  | {
      uid: string;
      email: string | null;
    }
  | null
  | undefined
>(undefined);

export function AuthContextProvider(props: {
  children: JSX.Element;
}): JSX.Element {
  const [auth, setUserAuth] = useState<
    { uid: string; email: string | null } | null | undefined
  >(undefined);

  useEffect(() => {
    const auth = getAuth();
    // We need to initialize with onAuthStateChanged
    // for persistence across sessions to work.
    onAuthStateChanged(
      auth,
      (user) => {
        if (user != null) {
          setUserAuth({
            uid: user.uid,
            email: user.email,
          });
        } else {
          setUserAuth(null);
        }

        // Keep the synchronous hint in step with the real auth state. Clearing
        // it on sign-out matters more than setting it: a stale hint would send
        // "/" into the app tree, which bounces back to "/" once auth resolves to
        // null. Clearing here means that bounce terminates in the landing page.
        setSessionHint(user != null);
      },
      (error) => {
        // Without this the listener's failures are silent and auth stays
        // undefined forever, which strands the viewer on a blank page.
        console.error("Firebase auth state listener failed.", error);
        setUserAuth(null);
      },
    );
  }, []);

  useEffect(() => {
    if (auth !== undefined) {
      return;
    }

    // Nothing above guarantees onAuthStateChanged ever fires -- a blocked
    // Firebase endpoint (privacy extensions do this), an offline client, or an
    // outage all leave it silent. Without this fallback, anything gated on auth
    // renders an empty div indefinitely. Degrading to "signed out" is
    // recoverable: the viewer gets the landing page or sign-in rather than
    // nothing at all.
    const timeoutId = setTimeout(() => {
      console.warn(
        `Firebase auth did not resolve within ${AUTH_RESOLUTION_TIMEOUT_MS}ms. ` +
          `Treating this session as signed out.`,
      );
      setUserAuth(null);
    }, AUTH_RESOLUTION_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [auth]);

  return (
    <AuthContext.Provider value={auth}>{props.children}</AuthContext.Provider>
  );
}
